import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import axios from "axios";
import { toast } from "sonner";
import {
  Flame, Upload, Timer, Activity, TrendingUp, Target, Zap, ArrowRight,
  ShieldCheck, Rewind, Sparkles, PlayCircle, BarChart3, LineChart as LineChartIcon
} from "lucide-react";

export default function Landing() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ signups: 12, exams_run: 34, attempts_graded: 47 });
  const [waitEmail, setWaitEmail] = useState("");
  const [waitBusy, setWaitBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/stats/public`).then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const joinWaitlist = async (e) => {
    e.preventDefault();
    if (!waitEmail) return;
    setWaitBusy(true);
    try {
      await axios.post(`${API}/waitlist`, { email: waitEmail, reason: "landing" });
      toast.success("You're on the waitlist. Talk soon.");
      setWaitEmail("");
    } catch {
      toast.error("Could not save. Try again.");
    } finally {
      setWaitBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900">
      {/* NAV */}
      <header className="sticky top-0 z-30 border-b border-stone-200 bg-white/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded bg-orange-600 text-white">
              <Flame className="h-4 w-4" strokeWidth={2.5} />
            </div>
            <span className="font-heading text-xl font-bold tracking-tight">ExamBro</span>
            <Badge variant="secondary" className="ml-2 font-mono text-[10px] uppercase tracking-[0.2em]">
              Beta
            </Badge>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm text-stone-600">
            <a href="#features" className="hover:text-stone-900">Features</a>
            <a href="#how" className="hover:text-stone-900">How it works</a>
            <a href="#pricing" className="hover:text-stone-900">Pricing</a>
            <a href="#stories" className="hover:text-stone-900">Stories</a>
          </nav>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => navigate("/login")} data-testid="landing-signin-btn">
              Sign in
            </Button>
            <Button
              onClick={() => navigate("/login")}
              className="bg-orange-600 hover:bg-orange-700 text-white"
              data-testid="landing-signup-btn"
            >
              Get started
            </Button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-stone-200">
        <div
          className="absolute inset-0 opacity-40 pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(rgba(28,25,23,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(28,25,23,0.05) 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />
        <div className="max-w-7xl mx-auto px-6 py-20 lg:py-32 grid lg:grid-cols-12 gap-12 items-center relative">
          <div className="lg:col-span-7 space-y-6">
            <div className="inline-flex items-center gap-2 rounded-full border border-stone-300 bg-white px-3 py-1 text-xs font-mono uppercase tracking-[0.2em] text-stone-600">
              <span className="h-1.5 w-1.5 rounded-full bg-orange-500 animate-pulse" />
              Live · learning exam technique
            </div>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.02] tracking-tight">
              Master the <span className="text-orange-600">exam room</span>.
            </h1>
            <p className="max-w-xl text-lg text-stone-600 leading-relaxed">
              AI coaching for how you <em>attempt</em> a paper, not just what you know. Upload any past paper, sit
              a real timed simulation, and get a behavioural report on pacing, pressure, decision quality, and the
              tiny habits that quietly cost you marks.
            </p>
            <div className="flex flex-wrap gap-3 pt-2">
              <Button
                size="lg"
                onClick={() => navigate("/login")}
                className="bg-orange-600 hover:bg-orange-700 text-white h-12 px-6"
                data-testid="hero-cta-btn"
              >
                Start free — 7 exams/month
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="h-12 px-6 border-stone-300"
                onClick={() => document.getElementById("how")?.scrollIntoView({ behavior: "smooth" })}
              >
                <PlayCircle className="mr-2 h-4 w-4" />
                See how it works
              </Button>
            </div>
            <div className="flex flex-wrap gap-6 pt-6 border-t border-stone-200">
              <StatMini n={stats.signups} label="Signups" />
              <StatMini n={stats.exams_run} label="Papers parsed" accent="text-emerald-600" />
              <StatMini n={stats.attempts_graded} label="Attempts graded" accent="text-orange-600" />
              <StatMini n={"MCQ · Integer"} label="Formats supported" />
            </div>
          </div>

          {/* Hero visual */}
          <div className="lg:col-span-5">
            <MockDashboard />
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="py-24 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone-500 mb-2">Features</div>
            <h2 className="font-heading text-4xl lg:text-5xl font-bold tracking-tight">
              More questions won't help. Measurable feedback will.
            </h2>
            <p className="text-stone-600 mt-4 leading-relaxed">
              Everything you need to train like a serious athlete, not just crunch more MCQs.
            </p>
          </div>

          {/* Core features */}
          <div className="mt-12">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-orange-600 mb-4">
              ⚡ Core loop
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <FeatureCard icon={Upload} accent="orange" title="AI Exam Builder" desc="Upload a PDF. Gemini 3 Flash auto-detects sections, marks, negative marking, and question types." />
              <FeatureCard icon={Timer} accent="stone" title="Real Exam Simulation" desc="Full-screen mode, timer, section timers, question palette (5 states), auto-submit at 0:00." />
              <FeatureCard icon={Activity} accent="emerald" title="Silent Telemetry" desc="Every view, revisit, answer-change, mark, and idle pause captured — without distracting you." />
            </div>
          </div>

          {/* Analytics */}
          <div className="mt-16">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-orange-600 mb-4">
              📊 Post-exam intelligence
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <FeatureCard icon={BarChart3} accent="rose" title="Deep Analytics" desc="Accuracy, speed, pressure index, decision quality, question selection, time wasted — all charted." />
              <FeatureCard icon={Rewind} accent="orange" title="Exam Replay" desc="Scrub the timeline of every question you visited, every change you made, every mark." />
              <FeatureCard icon={LineChartIcon} accent="emerald" title="Pacing Analytics" desc="Strava-style feed of your last attempts with sparklines + delta arrows vs your previous PR." />
              <FeatureCard icon={Sparkles} accent="emerald" title="AI Error Categorization" desc="Silly / concept gap / time pressure / second-guessing / guessing — with a coach tip per mistake." />
              <FeatureCard icon={Timer} accent="rose" title="Section Pacing Plan" desc="Based on your top-3 attempts, get section-by-section time recommendations for your next paper." />
              <FeatureCard icon={TrendingUp} accent="orange" title="Historical Trends" desc="Score %, accuracy, speed, decision quality, pressure — every metric tracked across every attempt." />
            </div>
          </div>

          {/* Interactivity */}
          <div className="mt-16">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-orange-600 mb-4">
              🎯 Sharpen your edge
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <FeatureCard icon={ShieldCheck} accent="emerald" title="Review Answers Tab" desc="See every question with your answer vs the AI's answer key. Options highlighted. Verdict clear." />
              <FeatureCard icon={PlayCircle} accent="orange" title="Challenge the AI" desc="Disagree with the AI answer? Claude Sonnet 4.5 arbitrates. If you're right, your score auto-updates." />
              <FeatureCard icon={ShieldCheck} accent="stone" title="Marking Scheme Presets" desc="One-click JEE 4/-1, NEET 4/0, SAT 1/0, GRE 1/-0.25 — or set your own. Applies to every question." />
            </div>
          </div>

          {/* Platform */}
          <div className="mt-16">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-orange-600 mb-4">
              🔐 Built to trust
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <FeatureCard icon={ShieldCheck} accent="stone" title="Google + Email Auth" desc="Sign in with your Google account or a plain email + password. HttpOnly cookies — XSS-safe." />
              <FeatureCard icon={Sparkles} accent="orange" title="Voice Announcements" desc="30/15/10/5/1 min countdown spoken aloud so you don't have to check the clock." />
              <FeatureCard icon={PlayCircle} accent="emerald" title="Feedback Loop" desc="One-click feedback modal — bug, feature, rating, contact-for-reply. Direct line to the team." />
              <FeatureCard icon={TrendingUp} accent="rose" title="Freemium — Free Forever" desc="7 exams / month on Free. No credit card. Premium waitlist for unlimited + priority parsing." />
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="py-24 border-b border-stone-200 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone-500 mb-2">How it works</div>
          <h2 className="font-heading text-4xl lg:text-5xl font-bold tracking-tight mb-12">
            From PDF to a report in under a minute.
          </h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { n: "01", t: "Upload", d: "Drop a MCQ / integer PDF. Any PYQ, mock, or coaching material." },
              { n: "02", t: "AI parses", d: "Gemini extracts sections, marks scheme, negative marking, options." },
              { n: "03", t: "Simulate", d: "Full-screen timed exam with palette, mark-for-review, voice cues." },
              { n: "04", t: "Report", d: "Score + behavioural metrics + replay + AI mistake analysis." },
            ].map((s) => (
              <div key={s.n} className="border-l-2 border-orange-600 pl-4">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-orange-600">{s.n}</div>
                <div className="font-heading text-xl font-semibold mt-2">{s.t}</div>
                <p className="text-sm text-stone-600 leading-relaxed mt-2">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STORIES */}
      <section id="stories" className="py-24 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone-500 mb-2">Stories</div>
          <h2 className="font-heading text-4xl lg:text-5xl font-bold tracking-tight mb-12">
            Aspirants who stopped guessing.
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                quote: "The 'correct→wrong changes' metric was a slap. I stopped second-guessing on SAT math and my accuracy jumped 18 points.",
                name: "Reserved for early user",
                tag: "SAT · United States",
              },
              {
                quote: "GRE quant is 60% pacing. ExamBro showed me I was burning 4 minutes on a single QC question. I cut that in half.",
                name: "Reserved for early user",
                tag: "GRE · India → US grad",
              },
              {
                quote: "GMAT verbal is brutal on pressure. The pressure index broke my score plateau. 720 → 750 in six weeks.",
                name: "Reserved for early user",
                tag: "GMAT · MBA applicant",
              },
              {
                quote: "Finally an app that treats PYQs like a training log. I know exactly which JEE section is bleeding my time.",
                name: "Reserved for early user",
                tag: "JEE Advanced · India",
              },
              {
                quote: "The AI mistake analysis called me out for guessing on 6 questions. Ruthless. Exactly what I needed for GATE prep.",
                name: "Reserved for early user",
                tag: "GATE CS · India",
              },
              {
                quote: "I was spending 4 minutes on one biology question and didn't realise. The replay timeline showed it in seconds.",
                name: "Reserved for early user",
                tag: "NEET · repeater",
              },
            ].map((t) => (
              <Card key={t.name + t.tag} className="p-6 rounded-md border-stone-200 shadow-none">
                <p className="text-stone-800 leading-relaxed">"{t.quote}"</p>
                <div className="mt-6 pt-4 border-t border-stone-200">
                  <div className="font-heading text-sm font-semibold">{t.name}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500 mt-1">{t.tag}</div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-24 border-b border-stone-200 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center max-w-xl mx-auto">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone-500 mb-2">Pricing</div>
            <h2 className="font-heading text-4xl lg:text-5xl font-bold tracking-tight mb-4">
              Free forever for casuals. Serious? Join the waitlist.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6 mt-12">
            <Card className="p-8 rounded-md border-stone-200 shadow-none">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone-500 mb-2">Free</div>
              <div className="flex items-baseline gap-1 mb-4">
                <span className="font-heading text-5xl font-bold">$0</span>
                <span className="text-stone-500">/month</span>
              </div>
              <ul className="space-y-3 text-sm">
                <PricingRow>7 exam uploads / month</PricingRow>
                <PricingRow>Full exam simulation + telemetry</PricingRow>
                <PricingRow>Post-exam analytics report</PricingRow>
                <PricingRow>3 months of history</PricingRow>
              </ul>
              <Button className="w-full mt-6 bg-stone-900 hover:bg-stone-800 text-white h-11" onClick={() => navigate("/login")}>
                Start free
              </Button>
            </Card>
            <Card className="p-8 rounded-md border-2 border-orange-600 shadow-none relative">
              <Badge className="absolute -top-3 left-6 bg-orange-600 text-white font-mono text-[10px] uppercase tracking-[0.2em]">
                Coming soon
              </Badge>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-orange-600 mb-2">Premium</div>
              <div className="flex items-baseline gap-1 mb-4">
                <span className="font-heading text-5xl font-bold">$10.99</span>
                <span className="text-stone-500">/month</span>
              </div>
              <ul className="space-y-3 text-sm">
                <PricingRow>Unlimited exam uploads</PricingRow>
                <PricingRow>Full analytics + AI error categorization</PricingRow>
                <PricingRow>Unlimited performance history</PricingRow>
                <PricingRow>Exam replay timeline</PricingRow>
                <PricingRow>Priority parsing queue</PricingRow>
              </ul>
              <form onSubmit={joinWaitlist} className="mt-6 flex gap-2">
                <Input
                  type="email"
                  placeholder="you@example.com"
                  value={waitEmail}
                  onChange={(e) => setWaitEmail(e.target.value)}
                  required
                  data-testid="waitlist-email"
                />
                <Button
                  type="submit"
                  disabled={waitBusy}
                  className="bg-orange-600 hover:bg-orange-700 text-white"
                  data-testid="waitlist-submit"
                >
                  Waitlist
                </Button>
              </form>
              <p className="text-xs text-stone-500 mt-3">Get 50% off for life when we launch.</p>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-orange-600 mb-3">Ready?</div>
          <h2 className="font-heading text-4xl lg:text-5xl font-bold tracking-tight">
            Every attempt sharpens your edge.
          </h2>
          <p className="text-stone-600 mt-4 max-w-xl mx-auto">
            The exam doesn't measure you — your last 20 attempts do. Start tracking today.
          </p>
          <Button
            size="lg"
            className="mt-8 bg-orange-600 hover:bg-orange-700 text-white h-12 px-8"
            onClick={() => navigate("/login")}
            data-testid="cta-bottom-btn"
          >
            Start your first exam
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      <footer className="border-t border-stone-200 py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between gap-4 text-sm text-stone-500">
          <div className="flex items-center gap-2">
            <div className="flex h-6 w-6 items-center justify-center rounded bg-orange-600 text-white">
              <Flame className="h-3 w-3" />
            </div>
            <span className="font-heading font-semibold text-stone-900">ExamBro</span>
            <span>© {new Date().getFullYear()}</span>
          </div>
          <div className="font-mono text-xs uppercase tracking-[0.2em]">
            Any MCQ or integer-answer paper — from SAT to JEE
          </div>
        </div>
      </footer>
    </div>
  );
}

function StatMini({ n, label, accent = "text-stone-900" }) {
  return (
    <div>
      <div className={`font-heading text-2xl font-bold tabular-nums ${accent}`}>{n}</div>
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500 mt-1">{label}</div>
    </div>
  );
}

function FeatureCard({ icon: Icon, title, desc, accent = "orange" }) {
  const cls = {
    orange: "bg-orange-50 text-orange-600",
    emerald: "bg-emerald-50 text-emerald-600",
    rose: "bg-rose-50 text-rose-600",
    stone: "bg-stone-100 text-stone-700",
  }[accent];
  return (
    <Card className="p-6 rounded-md border-stone-200 shadow-none hover:-translate-y-0.5 hover:shadow-md"
      style={{ transition: "transform 0.2s ease, box-shadow 0.2s ease" }}>
      <div className={`inline-flex h-10 w-10 items-center justify-center rounded-md ${cls}`}>
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="font-heading text-xl font-semibold mt-4">{title}</h3>
      <p className="text-sm text-stone-600 leading-relaxed mt-2">{desc}</p>
    </Card>
  );
}

function PricingRow({ children }) {
  return (
    <li className="flex items-start gap-3">
      <ShieldCheck className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
      <span>{children}</span>
    </li>
  );
}

function MockDashboard() {
  return (
    <div className="relative rounded-md border border-stone-200 bg-white shadow-xl overflow-hidden">
      <div className="border-b border-stone-200 px-4 py-3 flex items-center justify-between bg-stone-50">
        <div className="flex items-center gap-2">
          <div className="h-2 w-2 rounded-full bg-orange-500" />
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">Live report · Mock Paper</span>
        </div>
        <span className="font-mono text-xs text-stone-500 tabular-nums">02:47:31</span>
      </div>
      <div className="p-5 space-y-4">
        <div className="flex items-baseline gap-2">
          <span className="font-heading text-4xl font-bold text-orange-600 tabular-nums">241</span>
          <span className="font-mono text-lg text-stone-400">/300</span>
          <Badge className="ml-auto bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-emerald-200">+18 vs last</Badge>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {[
            { l: "Accuracy", v: "86%", c: "text-emerald-600" },
            { l: "Speed", v: "74", c: "text-orange-600" },
            { l: "Pressure", v: "22", c: "text-rose-600" },
          ].map((s) => (
            <div key={s.l} className="rounded-md border border-stone-200 p-3">
              <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-stone-500">{s.l}</div>
              <div className={`font-heading text-xl font-bold tabular-nums ${s.c}`}>{s.v}</div>
            </div>
          ))}
        </div>
        <div className="rounded-md border border-stone-200 p-3">
          <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-stone-500 mb-2">Score trend</div>
          <div className="h-16 flex items-end gap-1">
            {[35, 42, 51, 48, 63, 71, 68, 75, 82, 86].map((v, i) => (
              <div key={`bar-${i}-${v}`} className="flex-1 rounded-t bg-orange-500" style={{ height: `${v}%`, opacity: 0.4 + i * 0.06 }} />
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2 rounded-md border-l-4 border-amber-500 bg-amber-50 px-3 py-2">
          <Sparkles className="h-4 w-4 text-amber-600 flex-shrink-0" />
          <span className="text-xs text-stone-800">Trust your first instinct — you changed 4 correct answers to wrong ones.</span>
        </div>
      </div>
    </div>
  );
}
